import type { CarriedLine } from "../hud/panels";

/*
 * The people walking with you, client-side: what the panel calls them, and the
 * two sentences a press can be refused with.
 *
 * A module beside `witness/witness.ts` rather than more of it (`witness/arms.ts`'
 * reason, and the project's module rule applied to `web/src`): the witnessing
 * layer was at 2,650 code lines when this chunk opened. What lives here is the
 * part of a companionship that is a *rule about words* — everything else is a
 * read of the simulation's own answer.
 *
 * # Two rules this file is written under, and both are the phase's
 *
 * - **Nothing here decides who follows whom.** Whether somebody offers, whether
 *   a beckon lands and whether a follower has come as far as they will come are
 *   all the simulation's (`sim_core::companions`), and they arrive as a handle,
 *   a sentence and a sentence. This file writes the panel's punctuation.
 * - **No press the simulation refuses in silence** (the graybox ruling). The two
 *   refusals below are the two the sim makes that this client has the numbers
 *   for: the reach it takes somebody along from, and the size of a hero's
 *   company.
 */

/**
 * The two refusals a beckon can give.
 *
 * They are the two the client can *see coming* — the numbers cross the wall
 * (`beckon_reach_tiles`, `max_companions`) and the reads are already held — and
 * there are deliberately no others: every remaining reason the simulation would
 * refuse is a reason the **offer** does not stand at all, and an offer that does
 * not stand is a verb this client never draws. So there is no *they would not
 * come with you* sentence, because there is no press that produces it.
 */
export const REFUSED_TOO_FAR_TO_TAKE = "too far to take them along";
export const REFUSED_COMPANY_FULL = "you have all the company you can keep";

/**
 * The hands panel's last section: who is walking with the player.
 *
 * `handsLines`' shape exactly ({@link CarriedLine}, `witness/arms.ts`), and it
 * is in the panel rather than in a HUD slot of its own for the reason that file
 * gives about the wear verbs: **the panel is the menu**, and *who is with you*
 * is a thing a player checks standing still rather than a thing that has to be
 * on the screen while they walk. A blank row before the heading, like the worn
 * section above it.
 *
 * Empty for the overwhelming majority of sessions, in which nobody has ever
 * followed anybody — so the panel is byte for byte what it was before this
 * chunk until somebody joins.
 *
 * The names are the **simulation's** (`WitnessSource.figureName`); this composes
 * the heading and nothing else.
 */
export function companyLines(names: readonly string[]): CarriedLine[] {
  if (names.length === 0) {
    return [];
  }

  const lines: CarriedLine[] = [{ label: "" }, { label: "with you:" }];

  for (const name of names) {
    lines.push({ label: name });
  }

  return lines;
}
